import React from 'react';
import {
  Paper,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TextField,
  InputAdornment,
  TablePagination
} from '@mui/material';
import ShareIcon from '@mui/icons-material/Share';
import SearchIcon from '@mui/icons-material/Search';
import { AdminReferral } from '../../types/admin/AdminTypes';

interface ReferralsTableProps {
  referrals: AdminReferral[];
  searchTerm: string;
  onSearchChange: (value: string) => void;
  page: number;
  rowsPerPage: number;
  total: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
}

const getStatusColor = (status: string) => {
  switch (status) {
    case 'converted':
      return 'success';
    case 'pending':
      return 'warning';
    case 'expired':
      return 'error';
    default:
      return 'default';
  }
};

const getStatusLabel = (status: string) => {
  switch (status) {
    case 'converted':
      return 'Convertido';
    case 'pending':
      return 'Pendente';
    case 'expired':
      return 'Expirado';
    default:
      return status;
  }
};

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('pt-BR');
};

const formatCurrency = (value?: number) => {
  if (!value) return '-';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value);
};

const ReferralsTable: React.FC<ReferralsTableProps> = ({
  referrals,
  searchTerm,
  onSearchChange,
  page,
  rowsPerPage,
  total,
  onPageChange,
  onRowsPerPageChange
}) => {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 3,
        border: '1px solid #e0e0e0'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <ShareIcon sx={{ color: '#1976d2' }} />
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Indicações (Referrals)
        </Typography>
      </Box>

      {/* Filtros */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <TextField
          size="small"
          placeholder="Buscar indicação..."
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            )
          }}
          sx={{ minWidth: 250 }}
        />
      </Box>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Indicador</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Indicado</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Comissão</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Data</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {referrals.map((referral) => (
              <TableRow key={referral.id}>
                <TableCell>
                  <Box>
                    <Typography variant="body2" fontWeight={500}>
                      {referral.referrerName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {referral.referrerEmail}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Box>
                    <Typography variant="body2" fontWeight={500}>
                      {referral.referredName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {referral.referredEmail}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip
                    label={getStatusLabel(referral.status)}
                    color={getStatusColor(referral.status) as any}
                    size="small"
                  />
                </TableCell>
                <TableCell sx={{ color: '#4caf50', fontWeight: 500 }}>
                  {formatCurrency(referral.commission)}
                </TableCell>
                <TableCell>{formatDate(referral.createdAt)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={total}
        page={Math.max(page - 1, 0)}
        onPageChange={(_, newPage) => onPageChange(newPage + 1)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(event) => onRowsPerPageChange(Number(event.target.value))}
        rowsPerPageOptions={[10, 20, 50]}
        labelRowsPerPage="Linhas por página"
      />

      {referrals.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography color="text.secondary">
            Nenhuma indicação registrada
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default ReferralsTable;
